import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Calculator, GitCompareArrows, CalendarDays, BarChart3, ArrowRight, ArrowLeft, X, Sparkles, Rocket } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const STORAGE_KEY = 'onboarding_completed';
// Security audit (Feb 2026 · Batch 1): this key holds the literal
// string 'true' once the tour has finished — a perf cache only. The
// authoritative source is `users.preferences.onboarding_tour_completed`
// on the backend (re-synced on every login). No PII, no auth state.
// localStorage write is acceptable; clearing it just re-shows the
// tour on next cold load (which the backend pref then suppresses).
const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  {
    icon: Calculator,
    title: 'Estimator',
    color: '#4ABFCF',
    desc: 'Build data-driven LMS implementation estimates. Walk through a guided wizard — select your implementation type, set complexity, toggle components, and tune project factors to produce a detailed cost and effort breakdown by role.',
  },
  {
    icon: GitCompareArrows,
    title: 'Compare',
    color: '#3B82F6',
    desc: 'Place two or more estimates side-by-side to understand the real differences. See deltas across effort, cost, and every component group. Clone estimates to create What-If variants and compare them against the original.',
  },
  {
    icon: CalendarDays,
    title: 'Planner',
    color: '#8B5CF6',
    desc: 'Transform any estimate into a phased implementation timeline. See Gantt charts, resource heatmaps, and weekly loading curves. Use the What-If Simulator to explore alternative scenarios and save Snapshots for comparison.',
  },
  {
    icon: BarChart3,
    title: 'Insights',
    color: '#14B8A6',
    desc: 'Aggregate data across all your estimates and comparisons to reveal patterns and benchmarks. Filter by organization, industry, type, or complexity — and export branded PDF reports for stakeholders.',
  },
];

const TOUR_STEPS = [
  {
    type: 'welcome',
    title: 'Welcome to the Seertech Blueprint Studio',
    subtitle: 'Your complete toolkit for scoping, planning, and presenting LMS deployments.',
  },
  {
    type: 'spotlight',
    selector: '[data-testid="pinned-rail"]',
    fallbackSelector: '[data-testid="workspaces-heading"]',
    title: 'Pin your most-active estimates',
    desc: "Star up to 5 estimates to keep them front-and-centre at the top of Workspaces. Pinned cards show real-time \"activity since last visit\" badges — a one-glance feed of what your team changed while you were away.",
    position: 'below',
  },
  {
    type: 'spotlight',
    selector: '[data-testid="new-estimate-btn"]',
    title: 'Start a new estimate',
    desc: "Click here to spin up a brand-new estimate. You'll be walked through a 4-step wizard — choose your implementation type, set complexity, toggle components, and tune project factors. Results update in real-time.",
    position: 'below-left',
  },
  {
    type: 'spotlight',
    selector: '[data-testid="nav-insights"]',
    title: 'Zoom out with Insights',
    desc: "Once you have a few estimates saved, head to Insights for cross-portfolio analytics — benchmarks by industry, win/loss rates, approval response times, and AI-generated trend narratives you can export as a branded PDF.",
    position: 'below',
  },
  {
    type: 'finish',
    title: "You're All Set!",
    desc: 'Start by creating your first estimate — or try the Quick Start Demo on the Estimator page to see a full estimate in seconds. Every page has contextual guides you can toggle with the "Show Guides" button.',
  },
];

function SpotlightOverlay({ rect, onClick }) {
  if (!rect) return <div className="fixed inset-0 bg-black/60 z-[9998] transition-opacity duration-300" onClick={onClick} />;

  const pad = 8;
  const r = 12;
  const x = rect.left - pad;
  const y = rect.top - pad;
  const w = rect.width + pad * 2;
  const h = rect.height + pad * 2;

  return (
    <svg className="fixed inset-0 w-full h-full z-[9998] transition-opacity duration-300" onClick={onClick}>
      <defs>
        <mask id="spotlight-mask">
          <rect width="100%" height="100%" fill="white" />
          <rect x={x} y={y} width={w} height={h} rx={r} ry={r} fill="black" />
        </mask>
      </defs>
      <rect width="100%" height="100%" fill="rgba(0,0,0,0.6)" mask="url(#spotlight-mask)" />
      <rect x={x} y={y} width={w} height={h} rx={r} ry={r}
        fill="none" stroke="rgba(74,191,207,0.6)" strokeWidth="2" className="animate-pulse" />
    </svg>
  );
}

function TooltipCard({ step, currentIdx, totalSteps, onNext, onBack, onSkip, style }) {
  return (
    <div className="fixed z-[9999] transition-all duration-300 ease-out" style={style} data-testid="tour-tooltip">
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-[340px] overflow-hidden">
        <div className="bg-gradient-to-r from-[#0F2040] to-[#1a3a6e] px-5 py-3">
          <div className="flex items-center justify-between">
            <span className="text-white/60 text-[10px] font-medium tracking-wide uppercase">
              Step {currentIdx + 1} of {totalSteps}
            </span>
            <button onClick={onSkip} className="text-white/40 hover:text-white/80 transition-colors" data-testid="tour-skip">
              <X className="w-4 h-4" />
            </button>
          </div>
          <h3 className="text-white font-semibold text-sm mt-1">{step.title}</h3>
        </div>
        <div className="px-5 py-3">
          <p className="text-xs text-slate-600 leading-relaxed">{step.desc}</p>
        </div>
        <div className="px-5 pb-4 flex items-center justify-between">
          <div className="flex gap-1">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === currentIdx ? 'bg-[#4ABFCF]' : 'bg-slate-200'}`} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {currentIdx > 0 && (
              <button onClick={onBack}
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors px-2 py-1"
                data-testid="tour-back">
                <ArrowLeft className="w-3 h-3" /> Back
              </button>
            )}
            <button onClick={onNext}
              className="flex items-center gap-1 text-xs font-semibold text-white bg-[#4ABFCF] hover:bg-[#3AAAB9] px-3 py-1.5 rounded-lg transition-colors"
              data-testid="tour-next">
              {currentIdx === totalSteps - 1 ? 'Got it' : 'Next'}
              {currentIdx < totalSteps - 1 && <ArrowRight className="w-3 h-3" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function OnboardingTour({ active, onComplete }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState(null);
  const animFrame = useRef(null);

  const step = TOUR_STEPS[stepIdx];
  const spotlightSteps = TOUR_STEPS.filter(s => s.type === 'spotlight' || s.type === 'finish');

  const updateSpotlight = useCallback(() => {
    if (!step || step.type !== 'spotlight') {
      setSpotlightRect(null);
      return;
    }
    let el = document.querySelector(step.selector);
    if (!el && step.fallbackSelector) {
      el = document.querySelector(step.fallbackSelector);
    }
    if (el) {
      const rect = el.getBoundingClientRect();
      setSpotlightRect(rect);
    } else {
      // Target not rendered (e.g. Pinned rail for a brand-new user with
      // nothing pinned yet). Drop the spotlight and let the tooltip
      // fall back to the centred position handled below.
      setSpotlightRect(null);
    }
  }, [step]);

  useEffect(() => {
    if (!active) return;
    updateSpotlight();
    const onResize = () => updateSpotlight();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [active, updateSpotlight]);

  const goNext = useCallback(() => {
    if (stepIdx >= TOUR_STEPS.length - 1) {
      onComplete();
    } else {
      setStepIdx(i => i + 1);
    }
  }, [stepIdx, onComplete]);

  const goBack = useCallback(() => {
    setStepIdx(i => Math.max(0, i - 1));
  }, []);

  if (!active) return null;

  // Welcome screen
  if (step.type === 'welcome') {
    return (
      <>
        <div className="fixed inset-0 bg-black/60 z-[9998]" />
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid="tour-welcome">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden animate-in fade-in zoom-in-95 duration-300">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0F2040] to-[#1a3a6e] px-8 py-6 relative overflow-hidden">
              <div className="absolute top-2 right-3">
                <button onClick={onComplete} className="text-white/30 hover:text-white/70 transition-colors" data-testid="tour-skip">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-[#4ABFCF]/20 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-[#4ABFCF]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">{step.title}</h2>
                  <p className="text-white/60 text-xs mt-0.5">{step.subtitle}</p>
                </div>
              </div>
            </div>

            {/* Section cards */}
            <div className="p-6 space-y-3">
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-3">Four integrated sections</p>
              <div className="grid grid-cols-2 gap-3">
                {SECTIONS.map(s => (
                  <div key={s.title} className="border rounded-lg p-3 hover:shadow-sm transition-shadow">
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${s.color}15` }}>
                        <s.icon className="w-3.5 h-3.5" style={{ color: s.color }} />
                      </div>
                      <span className="text-sm font-semibold text-[#0F2040]">{s.title}</span>
                    </div>
                    <p className="text-[10px] leading-relaxed text-slate-500">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 pb-5 flex items-center justify-between">
              <button onClick={onComplete} className="text-xs text-slate-400 hover:text-slate-600 transition-colors" data-testid="tour-skip-intro">
                Skip tour
              </button>
              <button onClick={goNext}
                className="flex items-center gap-2 text-sm font-semibold text-white bg-[#4ABFCF] hover:bg-[#3AAAB9] px-5 py-2 rounded-lg transition-colors"
                data-testid="tour-start">
                Take the Tour <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Finish screen
  if (step.type === 'finish') {
    return (
      <>
        <div className="fixed inset-0 bg-black/60 z-[9998]" />
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid="tour-finish">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-[#0F2040] to-[#1a3a6e] px-8 py-8 text-center">
              <div className="w-16 h-16 rounded-2xl bg-[#4ABFCF]/20 flex items-center justify-center mx-auto mb-3">
                <Rocket className="w-8 h-8 text-[#4ABFCF]" />
              </div>
              <h2 className="text-xl font-bold text-white">{step.title}</h2>
            </div>
            <div className="p-6 text-center">
              <p className="text-sm text-slate-600 leading-relaxed">{step.desc}</p>
            </div>
            <div className="px-6 pb-6 flex items-center justify-between">
              <button onClick={goBack} className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors" data-testid="tour-back">
                <ArrowLeft className="w-3 h-3" /> Back
              </button>
              <button onClick={onComplete}
                className="text-sm font-semibold text-white bg-[#4ABFCF] hover:bg-[#3AAAB9] px-6 py-2.5 rounded-lg transition-colors"
                data-testid="tour-done">
                Start Exploring
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Spotlight steps
  const tooltipStyle = {};
  if (spotlightRect) {
    const pad = 8;
    if (step.position === 'below-left') {
      tooltipStyle.top = spotlightRect.bottom + pad + 12;
      tooltipStyle.right = window.innerWidth - spotlightRect.right - pad;
    } else {
      tooltipStyle.top = spotlightRect.bottom + pad + 12;
      tooltipStyle.left = Math.max(16, spotlightRect.left - 50);
    }
    if (tooltipStyle.left && tooltipStyle.left + 340 > window.innerWidth) {
      tooltipStyle.left = window.innerWidth - 356;
    }
  } else {
    tooltipStyle.top = '50%';
    tooltipStyle.left = '50%';
    tooltipStyle.transform = 'translate(-50%, -50%)';
  }

  return (
    <>
      <SpotlightOverlay rect={spotlightRect} onClick={() => {}} />
      <TooltipCard
        step={step}
        currentIdx={stepIdx}
        totalSteps={TOUR_STEPS.length}
        onNext={goNext}
        onBack={goBack}
        onSkip={onComplete}
        style={tooltipStyle}
      />
    </>
  );
}

export function useOnboarding() {
  const { user, setUser } = useAuth();
  const [active, setActive] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(true);
  // Track whether we've already kicked off the auto-launch effect for the
  // current session so navigating between routes doesn't re-fire the modal.
  const launchDecidedRef = useRef(false);

  useEffect(() => {
    // Wait for auth resolution. user === null means "still checking".
    if (user === null) return;
    if (launchDecidedRef.current) return;

    // Backend preference is authoritative for signed-in users — survives
    // localStorage clears, private-browsing, and cross-device sessions.
    const serverDone = !!(user && user.preferences && user.preferences.onboarding_tour_completed);

    let localDone = false;
    try {
      localDone = localStorage.getItem(STORAGE_KEY) === 'true';
    } catch { /* localStorage blocked */ }

    if (serverDone || localDone) {
      setHasCompleted(true);
      // If the server says done but localStorage doesn't, sync forward so
      // future cold loads on this browser short-circuit before the auth
      // check resolves.
      if (serverDone && !localDone) {
        try { localStorage.setItem(STORAGE_KEY, 'true'); } catch { /* ignore */ }
      }
      launchDecidedRef.current = true;
      return;
    }

    // First-time user (anywhere). Show the welcome modal once per session.
    setHasCompleted(false);
    launchDecidedRef.current = true;
    const t = setTimeout(() => setActive(true), 800);
    return () => clearTimeout(t);
  }, [user]);

  const complete = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, 'true'); } catch { /* ignore */ }
    setActive(false);
    setHasCompleted(true);

    // Persist to backend so this user never re-sees the tour after a
    // cache clear / new device. Best-effort — UI state is already updated.
    if (user && user.id) {
      axios
        .put(`${API_URL}/api/auth/preferences`, { onboarding_tour_completed: true })
        .then((res) => {
          // Mirror the saved preference into the AuthContext user so other
          // hooks (e.g., a re-mount of OnboardingTour after a route change)
          // see the latest value without a full /me refetch.
          if (res?.data?.preferences && setUser) {
            setUser((prev) => (prev ? { ...prev, preferences: res.data.preferences } : prev));
          }
        })
        .catch(() => { /* swallow — local flag is enough for this session */ });
    }
  }, [user, setUser]);

  const restart = useCallback(() => {
    setActive(true);
  }, []);

  return { active, complete, restart, hasCompleted };
}
